import { useState, useEffect } from 'react';
import { css, keyframes } from '@emotion/react';
import { useHistory } from 'react-router-dom';

const pageStyles = css`
  min-height: 100vh;
  background: #f9f8f8;
  font-family: 'FlodeskSans', sans-serif;
`;

/* ── Top Nav ── */

const headerStyles = css`
  position: sticky;
  top: 0;
  z-index: 10;
  width: 100%;
  height: 96px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 48px;
`;

const logoStyles = css`
  width: 63px;
  height: 13px;
`;

const logoContainerStyles = css`
  width: 280px;
`;

const mainNavStyles = css`
  display: flex;
  gap: 24px;
  align-items: center;
`;

const navActiveStyles = css`
  font-size: 14px;
  font-weight: 500;
  color: #121212;
  letter-spacing: 0.16px;
`;

const navInactiveStyles = css`
  font-size: 14px;
  font-weight: 500;
  color: rgba(60, 46, 42, 0.52);
`;

const accountStyles = css`
  width: 280px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const avatarStyles = css`
  width: 40px;
  height: 40px;
  border-radius: 100px;
  border: 1px solid rgba(55, 49, 47, 0.12);
  object-fit: cover;
`;

/* ── Content ── */

const contentStyles = css`
  max-width: 1122px;
  margin: 0 auto;
  padding: 72px 0;
`;

const headerRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 56px;
`;

const titlesStyles = css`
  display: flex;
  gap: 24px;
  align-items: center;
  font-size: 24px;
  font-weight: 500;
`;

const titleActiveStyles = css`
  color: #241a17;
`;

const titleInactiveStyles = css`
  color: rgba(61, 46, 41, 0.52);
`;

const buttonsStyles = css`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const textButtonStyles = css`
  display: flex;
  gap: 8px;
  align-items: center;
  height: 36px;
  padding: 0 8px;
  border-radius: 3px;
  font-size: 14px;
  font-weight: 500;
  color: #241a17;
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const newEmailButtonStyles = css`
  display: flex;
  gap: 12px;
  align-items: center;
  height: 40px;
  padding: 0 17px;
  border-radius: 6px;
  background: #241a17;
  color: #f9f8f8;
  font-size: 14px;
  font-weight: 500;
  border: none;
  cursor: pointer;

  &:hover {
    background: #3a2d28;
  }
`;

/* ── Cards Grid ── */

const gridStyles = css`
  display: flex;
  flex-wrap: wrap;
  gap: 48px;
`;

const cardStyles = css`
  width: 342px;
  background: #fff;
  border: 1px solid rgba(55, 49, 47, 0.12);
  border-radius: 10px;
  overflow: hidden;
  box-shadow:
    0 1.98px 3.06px -0.36px rgba(0, 0, 0, 0.05),
    0 1px 1px -0.1px rgba(0, 0, 0, 0.05);
`;

const cardImageStyles = css`
  width: 100%;
  height: 339px;
  object-fit: cover;
  display: block;
`;

const cardImageCroppedStyles = css`
  width: 133%;
  height: 177%;
  object-fit: cover;
  display: block;
  position: relative;
  left: -17%;
  top: -29%;
`;

const cardBodyStyles = css`
  padding: 16px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const cardTitleStyles = css`
  font-size: 14px;
  color: #241a17;
  line-height: 20px;
  margin: 0;
`;

const badgeRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const badgeDraftStyles = css`
  display: flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 112px;
  border: 1px solid rgba(55, 49, 47, 0.2);
  font-size: 12px;
  font-weight: 500;
  color: rgba(61, 46, 41, 0.52);
`;

const dotPulse = keyframes`
  0%, 60%, 100% { opacity: 0; }
  30% { opacity: 1; }
`;

const badgeSentDarkStyles = css`
  display: flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 112px;
  background: #241a17;
  font-size: 12px;
  font-weight: 500;
  color: #fff;
`;

const badgeTestingStyles = css`
  display: flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 112px;
  background: #dff0f6;
  font-size: 12px;
  font-weight: 500;
  color: #241a17;

  .dot {
    animation: ${dotPulse} 1.4s ease-in-out infinite;
  }
  .dot:nth-of-type(2) {
    animation-delay: 0.2s;
  }
  .dot:nth-of-type(3) {
    animation-delay: 0.4s;
  }
`;

const chartButtonStyles = css`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;

  img {
    width: 17px;
    height: 12px;
    transform: rotate(90deg);
    opacity: 0.35;
  }

  &:hover {
    background: #f8f7f7;

    img {
      opacity: 1;
    }
  }
`;

/* ── A/B Panel ── */

const growA = keyframes`
  from { height: 0; }
  to { height: 136px; }
`;

const growB = keyframes`
  from { height: 0; }
  to { height: 111px; }
`;

const fadeIn = keyframes`
  0% { opacity: 0; transform: scale(0.5); }
  60% { opacity: 1; transform: scale(1.12); }
  80% { transform: scale(0.95); }
  100% { opacity: 1; transform: scale(1); }
`;

const rocketHover = keyframes`
  0% { transform: translateY(0) rotate(-8deg); }
  25% { transform: translateY(-3px) rotate(-2deg); }
  50% { transform: translateY(0) rotate(4deg); }
  75% { transform: translateY(-2px) rotate(-4deg); }
  100% { transform: translateY(0) rotate(-8deg); }
`;

const abPanelStyles = css`
  width: 342px;
  height: 451px;
  background: #fff;
  border: 1px solid rgba(55, 49, 47, 0.12);
  border-radius: 10px;
  box-shadow:
    0 1.98px 3.06px -0.36px rgba(0, 0, 0, 0.05),
    0 1px 1px -0.1px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;

const abPanelHeaderStyles = css`
  padding: 20px 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const abPanelTitleStyles = css`
  font-size: 14px;
  font-weight: 500;
  color: #251b18;
  line-height: 20px;
  margin: 0;
`;

const abPanelSubtitleStyles = css`
  font-size: 14px;
  color: rgba(57, 48, 45, 0.52);
  line-height: 20px;
  margin: 0;
`;

const abPanelCloseStyles = css`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 12px;
  height: 12px;

  img {
    width: 12px;
    height: 12px;
  }

  &:hover {
    opacity: 0.6;
  }
`;

const abPanelContentStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 60px;
  position: absolute;
  top: calc(50% + 12px);
  left: 0;
  right: 0;
  transform: translateY(-50%);
`;

const abBarsRowStyles = css`
  display: flex;
  align-items: flex-end;
  gap: 0;
  height: 136px;
  width: 222px;
`;

const abBarContainerStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 111px;
  gap: 24px;
`;

const abBarAStyles = css`
  width: 87px;
  height: 136px;
  background: #8bb7f6;
  border-radius: 8px;
  position: relative;
  animation: ${growA} 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  overflow: visible;
`;

const abBarBStyles = css`
  width: 87px;
  height: 111px;
  background: #b7baf2;
  border-radius: 8px;
  animation: ${growB} 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  position: relative;
`;

const barBadgeStyles = css`
  position: absolute;
  bottom: 9px;
  left: 8px;
  width: 69px;
  height: 20px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 112px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #241a17;
`;

const rocketStyles = css`
  position: absolute;
  top: 12px;
  left: 12px;
  font-size: 18px;
  animation:
    ${fadeIn} 0.45s ease-out 0.2s both,
    ${rocketHover} 2.5s ease-in-out 0.7s infinite;
`;

const abPercentStyles = css`
  font-size: 30px;
  color: #000;
  line-height: 34px;
  margin: 0;
`;

const abViewDetailsStyles = css`
  position: absolute;
  bottom: 56px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 17px;
  border: 1px solid rgba(61, 46, 41, 0.31);
  border-radius: 6px;
  background: none;
  font-family: 'FlodeskSans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #241a17;
  cursor: pointer;

  &:hover {
    border-color: rgba(61, 46, 41, 0.5);
  }
`;

const CARDS = [
  { image: '/card-2.png', title: 'Personal welcome message', badge: 'draft' as const, crop: false },
  {
    image: '/card-3.png',
    title: 'Personal welcome message',
    badge: 'sent-dark' as const,
    crop: true,
  },
  {
    image: '/card-4.png',
    title: 'Personal welcome message',
    badge: 'testing' as const,
    crop: false,
  },
  { image: '/card-4.png', title: 'Personal welcome message', badge: 'sent' as const, crop: false },
  { image: '/card-1.png', title: 'Personal welcome message', badge: 'sent' as const, crop: false },
];

export default function Dashboard() {
  const history = useHistory();
  const [abPanelOpen, setAbPanelOpen] = useState(false);
  const [showRocket, setShowRocket] = useState(false);

  useEffect(() => {
    if (abPanelOpen) {
      setShowRocket(false);
      const timer = setTimeout(() => setShowRocket(true), 200);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [abPanelOpen]);

  return (
    <div css={pageStyles}>
      <header css={headerStyles}>
        <div css={logoContainerStyles}>
          <img css={logoStyles} src="/flodesk-logo.svg" alt="Flodesk" />
        </div>
        <nav css={mainNavStyles}>
          <span css={navActiveStyles}>Emails</span>
          <span css={navInactiveStyles}>Forms</span>
          <span css={navInactiveStyles}>Workflows</span>
          <span css={navInactiveStyles}>Checkouts</span>
          <span css={navInactiveStyles}>Audience</span>
          <span css={navInactiveStyles}>Analytics</span>
        </nav>
        <div css={accountStyles}>
          <img css={avatarStyles} src="/avatar.png" alt="Account" />
        </div>
      </header>

      <div css={contentStyles}>
        <div css={headerRowStyles}>
          <div css={titlesStyles}>
            <span css={titleActiveStyles}>My emails</span>
            <span css={titleInactiveStyles}>Folders</span>
          </div>
          <div css={buttonsStyles}>
            <button type="button" css={textButtonStyles}>
              Search
            </button>
            <button type="button" css={textButtonStyles}>
              Sort ▾
            </button>
            <button type="button" css={textButtonStyles}>
              Status ▾
            </button>
            <button type="button" css={newEmailButtonStyles}>
              + New email
            </button>
          </div>
        </div>

        <div css={gridStyles}>
          {CARDS.map((card, i) => {
            // Show the A/B panel instead of the 3rd card (index 2) when open
            if (i === 2 && abPanelOpen) {
              return (
                <div key="ab-panel" css={abPanelStyles}>
                  <div css={abPanelHeaderStyles}>
                    <div>
                      <p css={abPanelTitleStyles}>A/B open rates</p>
                      <p css={abPanelSubtitleStyles}>2h 15m left</p>
                    </div>
                    <button
                      type="button"
                      css={abPanelCloseStyles}
                      onClick={() => setAbPanelOpen(false)}
                    >
                      <img src="/close-icon.svg" alt="Close" />
                    </button>
                  </div>
                  <div css={abPanelContentStyles}>
                    <div css={abBarsRowStyles}>
                      <div css={abBarContainerStyles}>
                        <div css={abBarAStyles}>
                          {showRocket && <span css={rocketStyles}>🚀</span>}
                          <span css={barBadgeStyles}>Variant A</span>
                        </div>
                        <p css={abPercentStyles}>45%</p>
                      </div>
                      <div css={abBarContainerStyles}>
                        <div css={abBarBStyles}>
                          <span css={barBadgeStyles}>Variant B</span>
                        </div>
                        <p css={abPercentStyles}>37%</p>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    css={abViewDetailsStyles}
                    onClick={() => history.push('/ab-results')}
                  >
                    View details
                  </button>
                </div>
              );
            }

            return (
              <div key={`${card.title}-${card.badge}-${card.image}`} css={cardStyles}>
                <div
                  css={css`
                    width: 100%;
                    height: 339px;
                    overflow: hidden;
                  `}
                >
                  <img
                    css={card.crop ? cardImageCroppedStyles : cardImageStyles}
                    src={card.image}
                    alt={card.title}
                  />
                </div>
                <div css={cardBodyStyles}>
                  <p css={cardTitleStyles}>{card.title}</p>
                  <div css={badgeRowStyles}>
                    {card.badge === 'draft' && (
                      <span css={badgeDraftStyles}>Draft last edited 2 hours ago</span>
                    )}
                    {card.badge === 'testing' && (
                      <>
                        <span css={badgeTestingStyles}>
                          A/B testing<span className="dot">.</span>
                          <span className="dot">.</span>
                          <span className="dot">.</span>
                        </span>
                        <button
                          type="button"
                          css={chartButtonStyles}
                          onClick={() => setAbPanelOpen(true)}
                        >
                          <img src="/chart-icon.svg" alt="Analytics" />
                        </button>
                      </>
                    )}
                    {card.badge === 'sent-dark' && (
                      <>
                        <span css={badgeSentDarkStyles}>Sent</span>
                        <button
                          type="button"
                          css={chartButtonStyles}
                          onClick={() => setAbPanelOpen(true)}
                        >
                          <img src="/chart-icon.svg" alt="Analytics" />
                        </button>
                      </>
                    )}
                    {card.badge === 'sent' && (
                      <>
                        <span css={badgeDraftStyles}>Sent to 11 on Mar 19</span>
                        <button type="button" css={chartButtonStyles}>
                          <img src="/chart-icon.svg" alt="Analytics" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
